/**
 * Module-level side-table for non-serializable refs.
 *
 * Why: AbortControllers and `setTimeout` handles cannot be cloned, would
 * confuse Zustand devtools/snapshots, and shouldn't trigger React
 * subscription re-renders. We keep them in a per-module `Map<uploadId, …>`.
 *
 * Lifecycle:
 *   - `attach(id, handle)`: on enqueue.
 *   - `release(id)`: on dismiss — clears any pending dismiss timer AND
 *     deletes the entry, so nothing leaks past the ≤5s release budget.
 *   - `_reset()`: clears every pending dismiss timer and empties the table.
 *     Used by tests AND by the dev-only HMR dispose hook below, so it is
 *     NOT a TEST-ONLY helper.
 *   - `_size()`: test-only helper; never called from app code.
 */

export interface Handle {
  abortController: AbortController;
  dismissTimeout: ReturnType<typeof setTimeout> | null;
}

const table = new Map<string, Handle>();

export const sideTable = {
  attach(id: string, handle: Handle): void {
    table.set(id, handle);
  },

  get(id: string): Handle | undefined {
    return table.get(id);
  },

  getSignal(id: string): AbortSignal | undefined {
    return table.get(id)?.abortController.signal;
  },

  /**
   * Remove an entry. Clears its pending dismiss timer (if any). No-op when
   * the id is unknown, so cleanup is safe to invoke unconditionally.
   */
  release(id: string): void {
    const handle = table.get(id);
    if (!handle) return;
    // `clearTimeout(undefined)` is a documented no-op; the `?? undefined`
    // dance just keeps TS happy because handle.dismissTimeout is
    // `Timeout | null`.
    clearTimeout(handle.dismissTimeout ?? undefined);
    table.delete(id);
  },

  /**
   * Clear every pending dismiss timer and empty the table.
   *
   * Callers:
   *   - tests (between cases, to keep the module singleton isolated);
   *   - the dev-only HMR dispose hook below, which runs before Vite swaps
   *     in the new module instance.
   *
   * Does NOT abort in-flight axios uploads (we don't call
   * `handle.abortController.abort()`). That is INTENTIONAL: on HMR the
   * user's tab is reloading anyway and the queue state is throwaway —
   * letting any open POST complete or fail naturally avoids spurious
   * `CanceledError`s in the dev console for requests the user did not
   * cancel. In tests the per-test `_reset` runs after axios has been
   * fully mocked / settled, so there's nothing live to abort.
   */
  _reset(): void {
    for (const handle of table.values()) {
      clearTimeout(handle.dismissTimeout ?? undefined);
    }
    table.clear();
  },

  /** TEST ONLY — observe the entry count. */
  _size(): number {
    return table.size;
  },
};

/**
 * Dev-only HMR cleanup.
 *
 * Vite HMR re-evaluates this module on every save of any file in its
 * dependency graph. The new module instance gets a fresh `table` Map,
 * but the OLD module's pending `setTimeout(dismiss, …)` handles still
 * fire — and they reference the prior store closure. Symptoms: stale
 * dismiss timers firing against detached state, "ghost" items in the
 * old tray instance, occasional uncaught errors in the dev console.
 *
 * `import.meta.hot?.dispose` runs before the new module instance loads;
 * we clear every pending timer there. In production builds
 * `import.meta.hot` is `undefined`, so this is a no-op there.
 */
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    sideTable._reset();
  });
}
